import { Component } from '@theme/component';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {HTMLElement | null} */
  #lockOwner = null;

  #cartItems() {
    return this.closest('cart-items-component');
  }

  #isCartMutationBusy() {
    const cartItems = this.#cartItems();
    if (!(cartItems instanceof HTMLElement)) return false;
    if (typeof cartItems.isMutationBusy === 'function') return cartItems.isMutationBusy();
    return cartItems.getAttribute('aria-busy') === 'true';
  }

  #setCartBusy(busy) {
    const cartItems = busy ? this.#cartItems() : this.#lockOwner || this.#cartItems();
    if (!(cartItems instanceof HTMLElement)) return;
    if (busy) this.#lockOwner = cartItems;
    if (typeof cartItems.setMutationBusy === 'function') {
      cartItems.setMutationBusy('discount', busy);
      if (!busy) this.#lockOwner = null;
      return;
    }

    cartItems.toggleAttribute('data-discount-busy', busy);
    const remainsBusy =
      cartItems.hasAttribute('data-discount-busy') ||
      cartItems.hasAttribute('data-upsell-busy') ||
      cartItems.hasAttribute('data-benefits-busy') ||
      cartItems.hasAttribute('data-cart-update-busy');
    cartItems.classList.toggle('cart-items-disabled', remainsBusy);
    if (remainsBusy) cartItems.setAttribute('aria-busy', 'true');
    else cartItems.removeAttribute('aria-busy');
    if (!busy) this.#lockOwner = null;
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;
    if (this.#isCartMutationBusy()) return;

    const discountCodeValue = discountCode.value;
    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.includes(discountCodeValue)) return;

    const abortController = this.#createAbortController();
    this.#setCartBusy(true);

    try {
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const newHtml = data.sections?.[this.dataset.sectionId];
      if (!response.ok || !Array.isArray(data.discount_codes) || typeof newHtml !== 'string') {
        throw new Error('Invalid cart discount response');
      }

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        discountCode.value = '';
        this.#handleDiscountError('discount_code');
        return;
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, newHtml, { mode: 'hydration' });
      this.#refreshCartBusyState();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        await this.#reconcileSection();
        this.#handleDiscountError('discount_code');
      }
    } finally {
      this.#activeFetch = null;
      this.#setCartBusy(false);
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;
    if (this.#isCartMutationBusy()) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();
    this.#setCartBusy(true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      const newHtml = data.sections?.[this.dataset.sectionId];
      if (!response.ok || !Array.isArray(data.discount_codes) || typeof newHtml !== 'string') {
        throw new Error('Invalid cart discount response');
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      await morphSection(this.dataset.sectionId, newHtml, { mode: 'hydration' });
      this.#refreshCartBusyState();
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        await this.#reconcileSection();
        this.#handleDiscountError('discount_code');
      }
    } finally {
      this.#activeFetch = null;
      this.#setCartBusy(false);
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const currentSection =
      typeof this.dataset.sectionId === 'string'
        ? document.getElementById(`shopify-section-${this.dataset.sectionId}`)
        : null;
    const currentComponent = currentSection?.querySelector('cart-discount-component');
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } =
      currentComponent instanceof CartDiscount ? currentComponent.refs : this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    if (!(cartDiscountError instanceof HTMLElement) || !(target instanceof HTMLElement)) return;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  async #reconcileSection() {
    if (typeof this.dataset.sectionId !== 'string') return;
    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false);
      await morphSection(this.dataset.sectionId, html, { mode: 'hydration' });
      this.#refreshCartBusyState();
    } catch {
      // Mantém o erro original visível; a próxima mutação fará novo render.
    }
  }

  #refreshCartBusyState() {
    const cartItems = this.#lockOwner || this.#cartItems();
    if (typeof cartItems?.refreshMutationBusyState === 'function') {
      cartItems.refreshMutationBusyState();
    }
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
